import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { API } from 'aws-amplify';
import { useTranslation } from 'react-i18next';
import { GET_PUBLIC_CAMPAIGNS } from '../../graphql/queries';
import { useAuthStore } from '../../store/authStore';
import GlobalNavBar from '../layout/GlobalNavBar';
import CampaignGridCard from '../campaigns/CampaignGridCard';
import { trackCtaClick } from '../../utils/analytics';

/* ─── Types ─── */
interface Campaign {
  campaignId: string;
  campaignName?: string;
  brandName?: string;
  campaignCategory?: string;
  representativeImages?: { fileKey?: string; fileUrl?: string; fileName?: string; fileSize?: number; fileType?: string }[];
  contentChannels?: string[];
  contentChannel?: string;
  rewardAmount?: number;
  recruitmentPeriod?: { startDate?: string; endDate?: string; alwaysRecruiting?: boolean };
  numberOfRecruits?: number;
  providedValueDescription?: string;
  status?: string;
  campaignType?: string;
  contentTypes?: string[];
  productName?: string;
  recommendedKeywords?: string[];
  createdAt?: string;
}

/* ─── Pure helper for property testing ─── */
export function sliceCampaigns(campaigns: Campaign[], max: number = 6): Campaign[] {
  return campaigns.slice(0, max);
}

/* ═══════════════════════════════════════════
   HeroSection
   ═══════════════════════════════════════════ */
const HeroSection: React.FC = () => {
  const { isAuthenticated } = useAuthStore();
  const { t } = useTranslation('common');

  const brandCtaLink = isAuthenticated ? '/campaigns/register' : '/register?type=host';
  // Influencer CTA always lands on the influencer landing page. The
  // landing page itself decides what to show next based on auth state
  // (sign up flow for guests, /discovery shortcut for logged-in
  // influencers via its useSignupTarget hook).
  const influencerCtaLink = '/influencer';

  return (
    <section className="bg-black text-white py-24">
      <div className="max-w-[1200px] mx-auto px-4 text-center">
        {/* Badge pill */}
        <span className="inline-block px-4 py-1.5 text-xs font-medium text-white/80 bg-white/10 rounded-full mb-6">
          {t('mainPage.hero.badge')}
        </span>

        {/* Headline */}
        <h1 className="text-3xl md:text-5xl font-bold leading-tight mb-4">
          {t('mainPage.hero.headline')}
        </h1>

        {/* Subheadline */}
        <p className="text-base md:text-lg text-gray-400 max-w-2xl mx-auto mb-10">
          {t('mainPage.hero.subheadline')}
        </p>

        {/* CTA buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to={brandCtaLink}
            onClick={() =>
              trackCtaClick({
                cta_text: t('mainPage.hero.brandCta'),
                cta_location: 'hero',
              })
            }
            className="px-8 py-3 text-sm font-semibold text-black bg-white rounded-lg hover:bg-gray-200 transition-colors"
          >
            {t('mainPage.hero.brandCta')}
          </Link>
          <Link
            to={influencerCtaLink}
            onClick={() =>
              trackCtaClick({
                cta_text: t('mainPage.hero.influencerCta'),
                cta_location: 'hero',
              })
            }
            className="px-8 py-3 text-sm font-semibold text-white border border-white/30 rounded-lg hover:bg-white/10 transition-colors"
          >
            {t('mainPage.hero.influencerCta')}
          </Link>
        </div>
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════
   WhyMatchgoSection
   ═══════════════════════════════════════════ */
const WhyMatchgoSection: React.FC = () => {
  const { t } = useTranslation('common');

  const cards = [
    { icon: '📍', titleKey: 'mainPage.whyMatchgo.card1Title', descKey: 'mainPage.whyMatchgo.card1Desc', highlightKey: 'mainPage.whyMatchgo.card1Highlight' },
    { icon: '✨', titleKey: 'mainPage.whyMatchgo.card2Title', descKey: 'mainPage.whyMatchgo.card2Desc', highlightKey: 'mainPage.whyMatchgo.card2Highlight' },
    { icon: '⚡', titleKey: 'mainPage.whyMatchgo.card3Title', descKey: 'mainPage.whyMatchgo.card3Desc', highlightKey: 'mainPage.whyMatchgo.card3Highlight' },
  ];

  return (
    <section className="py-12 bg-white">
      <div className="max-w-[1200px] mx-auto px-4">
        {/* Heading */}
        <p className="text-sm font-semibold text-gray-500 tracking-widest mb-2">
          {t('mainPage.whyMatchgo.heading')}
        </p>
        <h2 className="text-2xl md:text-3xl font-bold mb-3">
          {t('mainPage.whyMatchgo.subtitle')}
        </h2>
        <p className="text-gray-500 mb-6">
          {t('mainPage.whyMatchgo.description')}
        </p>

        {/* Feature cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {cards.map((card) => (
            <div key={card.titleKey} className="bg-[#f7f4ef] rounded-2xl p-6">
              <span className="text-3xl mb-4 block">{card.icon}</span>
              <h3 className="text-lg font-bold mb-2">{t(card.titleKey)}</h3>
              <p className="text-sm text-gray-600 mb-4">{t(card.descKey)}</p>
              <span className="inline-block px-3 py-1 text-xs font-medium bg-white/70 rounded-full text-gray-700">
                {t(card.highlightKey)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════
   ForWhoSection
   ═══════════════════════════════════════════ */
const ForWhoSection: React.FC = () => {
  const { t } = useTranslation('common');

  return (
    <section className="py-12 bg-white">
      <div className="max-w-[1200px] mx-auto px-4">
        {/* Heading */}
        <p className="text-sm font-semibold text-gray-500 tracking-widest mb-6">
          {t('mainPage.forWho.heading')}
        </p>

        {/* Two cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Brand card */}
          <div className="bg-[#f7eff7] rounded-2xl p-8">
            <span className="inline-block px-3 py-1 text-xs font-medium bg-white/60 rounded-full text-purple-700 mb-4">
              {t('mainPage.forWho.brandLabel')}
            </span>
            <h3 className="text-xl font-bold mb-4">{t('mainPage.forWho.brandTitle')}</h3>
            <ul className="space-y-2">
              <li className="flex items-start gap-2 text-sm text-gray-700">
                <span className="text-purple-500 mt-0.5">•</span>
                {t('mainPage.forWho.brandBullet1')}
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-700">
                <span className="text-purple-500 mt-0.5">•</span>
                {t('mainPage.forWho.brandBullet2')}
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-700">
                <span className="text-purple-500 mt-0.5">•</span>
                {t('mainPage.forWho.brandBullet3')}
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-700">
                <span className="text-purple-500 mt-0.5">•</span>
                {t('mainPage.forWho.brandBullet4')}
              </li>
            </ul>
          </div>

          {/* Influencer card */}
          <div className="bg-[#eff7f4] rounded-2xl p-8">
            <span className="inline-block px-3 py-1 text-xs font-medium bg-white/60 rounded-full text-green-700 mb-4">
              {t('mainPage.forWho.influencerLabel')}
            </span>
            <h3 className="text-xl font-bold mb-4">{t('mainPage.forWho.influencerTitle')}</h3>
            <ul className="space-y-2">
              <li className="flex items-start gap-2 text-sm text-gray-700">
                <span className="text-green-500 mt-0.5">•</span>
                {t('mainPage.forWho.influencerBullet1')}
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-700">
                <span className="text-green-500 mt-0.5">•</span>
                {t('mainPage.forWho.influencerBullet2')}
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-700">
                <span className="text-green-500 mt-0.5">•</span>
                {t('mainPage.forWho.influencerBullet3')}
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-700">
                <span className="text-green-500 mt-0.5">•</span>
                {t('mainPage.forWho.influencerBullet4')}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════
   HowItWorksSection
   ═══════════════════════════════════════════ */
const HowItWorksSection: React.FC = () => {
  const { t } = useTranslation('common');

  const steps = [
    { num: 1, roleKey: 'mainPage.howItWorks.step1Role', titleKey: 'mainPage.howItWorks.step1Title', descKey: 'mainPage.howItWorks.step1Desc' },
    { num: 2, roleKey: 'mainPage.howItWorks.step2Role', titleKey: 'mainPage.howItWorks.step2Title', descKey: 'mainPage.howItWorks.step2Desc' },
    { num: 3, roleKey: 'mainPage.howItWorks.step3Role', titleKey: 'mainPage.howItWorks.step3Title', descKey: 'mainPage.howItWorks.step3Desc' },
    { num: 4, roleKey: 'mainPage.howItWorks.step4Role', titleKey: 'mainPage.howItWorks.step4Title', descKey: 'mainPage.howItWorks.step4Desc' },
    { num: 5, roleKey: 'mainPage.howItWorks.step5Role', titleKey: 'mainPage.howItWorks.step5Title', descKey: 'mainPage.howItWorks.step5Desc' },
  ];

  return (
    <section className="py-12 bg-white">
      <div className="max-w-[1200px] mx-auto px-4">
        {/* Heading */}
        <p className="text-sm font-semibold text-gray-500 tracking-widest mb-2">
          {t('mainPage.howItWorks.heading')}
        </p>
        <h2 className="text-2xl md:text-3xl font-bold mb-3">
          {t('mainPage.howItWorks.subtitle')}
        </h2>
        <p className="text-gray-500 mb-8">
          {t('mainPage.howItWorks.description')}
        </p>

        {/* Steps — stacked cards */}
        <div className="flex flex-col md:flex-row gap-4">
          {steps.map((step, idx) => (
            <div
              key={step.num}
              className="bg-white rounded-2xl shadow-sm border p-6 flex-1 relative"
              style={{ marginTop: idx > 0 ? undefined : 0 }}
            >
              {/* Numbered circle */}
              <div className="bg-black text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mb-3">
                {step.num}
              </div>
              {/* Role label */}
              <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                {t(step.roleKey)}
              </span>
              {/* Title */}
              <h3 className="text-base font-bold mt-1 mb-2">{t(step.titleKey)}</h3>
              {/* Description */}
              <p className="text-sm text-gray-500">{t(step.descKey)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── Travel Categories Constant ─── */
export const TRAVEL_CATEGORIES = [
  { key: 'accommodation', titleKey: 'mainPage.travelOptimized.hotelTitle', descKey: 'mainPage.travelOptimized.hotelDesc', emoji: '🏨' },
  { key: 'tour', titleKey: 'mainPage.travelOptimized.tourTitle', descKey: 'mainPage.travelOptimized.tourDesc', emoji: '🗺️' },
  { key: 'restaurant', titleKey: 'mainPage.travelOptimized.fnbTitle', descKey: 'mainPage.travelOptimized.fnbDesc', emoji: '🍽️' },
  { key: 'ticket', titleKey: 'mainPage.travelOptimized.eventTitle', descKey: 'mainPage.travelOptimized.eventDesc', emoji: '🎭' },
] as const;

/* ═══════════════════════════════════════════
   TravelOptimizedSection
   ═══════════════════════════════════════════ */
const TravelOptimizedSection: React.FC = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  return (
    <section className="py-12 bg-white">
      <div className="max-w-[1200px] mx-auto px-4">
        <p className="text-sm font-semibold text-gray-500 tracking-widest mb-2">
          {t('mainPage.travelOptimized.heading')}
        </p>
        <h2 className="text-2xl md:text-3xl font-bold mb-3">
          {t('mainPage.travelOptimized.subtitle')}
        </h2>
        <p className="text-gray-500 mb-6">
          {t('mainPage.travelOptimized.description')}
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {TRAVEL_CATEGORIES.map((cat) => (
            <div
              key={cat.key}
              role="button"
              tabIndex={0}
              className="bg-[#f7f4ef] rounded-2xl p-6 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(`/discovery?category=${cat.key}`)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate(`/discovery?category=${cat.key}`); }}
            >
              <span className="text-3xl mb-4 block">{cat.emoji}</span>
              <h3 className="text-base font-bold mb-1">{t(cat.titleKey)}</h3>
              <p className="text-sm text-gray-600">{t(cat.descKey)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════
   CampaignsSection
   ═══════════════════════════════════════════ */
interface CampaignsSectionProps {
  campaigns: Campaign[];
  loading: boolean;
}

const CampaignsSection: React.FC<CampaignsSectionProps> = ({ campaigns, loading }) => {
  const { t } = useTranslation('common');

  return (
    <section className="py-12 bg-[#fafafa]">
      <div className="max-w-[1200px] mx-auto px-4">
        <p className="text-sm font-semibold text-gray-500 tracking-widest mb-2">
          {t('mainPage.campaigns.heading')}
        </p>
        <h2 className="text-2xl md:text-3xl font-bold mb-6">
          {t('mainPage.campaigns.subtitle')}
        </h2>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="bg-gray-200 rounded-xl h-72 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {campaigns.slice(0, 4).map((campaign, idx) => (
              <CampaignGridCard
                key={campaign.campaignId}
                campaign={campaign}
                index={idx}
                linkPrefix="/campaigns"
              />
            ))}
          </div>
        )}

        <div className="text-center">
          <Link
            to="/discovery"
            className="inline-block px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors mb-6"
          >
            {t('mainPage.campaigns.viewMore')}
          </Link>
          <p className="text-sm text-gray-400">
            {t('mainPage.campaigns.tagline')}
          </p>
        </div>
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════
   CTASection
   ═══════════════════════════════════════════ */
const CTASection: React.FC = () => {
  const { t } = useTranslation('common');

  return (
    <section className="py-12 bg-white">
      <div className="max-w-[1200px] mx-auto px-4">
        <div className="text-center mb-6">
          <h2 className="text-2xl md:text-3xl font-bold mb-3">
            {t('mainPage.cta.headline')}
          </h2>
          <p className="text-gray-500">
            {t('mainPage.cta.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Host card */}
          <Link to="/register?type=host" className="block bg-[#26215c] rounded-2xl p-8 text-white hover:opacity-90 transition-opacity">
            <span className="inline-block px-3 py-1 text-xs font-medium bg-white/10 rounded-full text-[#7f77dd] mb-4">
              {t('mainPage.cta.hostLabel')}
            </span>
            <h3 className="text-xl font-bold mb-2">{t('mainPage.cta.hostTitle')}</h3>
            <p className="text-sm text-gray-300">{t('mainPage.cta.hostDesc')}</p>
          </Link>

          {/* Influencer card */}
          <Link to="/register?type=influencer" className="block bg-[#04342c] rounded-2xl p-8 text-white hover:opacity-90 transition-opacity">
            <span className="inline-block px-3 py-1 text-xs font-medium bg-white/10 rounded-full text-[#1d9e75] mb-4">
              {t('mainPage.cta.influencerLabel')}
            </span>
            <h3 className="text-xl font-bold mb-2">{t('mainPage.cta.influencerTitle')}</h3>
            <p className="text-sm text-gray-300">{t('mainPage.cta.influencerDesc')}</p>
          </Link>
        </div>
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════
   FooterSection
   ═══════════════════════════════════════════ */
const FooterSection: React.FC = () => {
  const { t } = useTranslation('common');

  return (
    <footer id="footer" className="py-8 bg-white border-t border-gray-200">
      <div className="max-w-[1200px] mx-auto px-4 text-center">
        <p className="text-lg font-bold mb-1">{t('mainPage.footer.brand')}</p>
        <p className="text-sm text-gray-400">{t('mainPage.footer.tagline')}</p>
      </div>
    </footer>
  );
};

/* ═══════════════════════════════════════════
   MainPage (wrapper)
   ═══════════════════════════════════════════ */
const MainPage: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchedRef = React.useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    const fetchCampaigns = async () => {
      try {
        const response = await API.graphql({
          query: GET_PUBLIC_CAMPAIGNS,
          authMode: 'AWS_IAM',
        });
        const fetched = (response as any).data?.getPublicCampaigns?.campaigns || [];
        setCampaigns(sliceCampaigns(fetched));
      } catch (err) {
        console.error('Failed to fetch campaigns:', err);
        setCampaigns([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCampaigns();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <GlobalNavBar variant="dark" />
      <HeroSection />
      <WhyMatchgoSection />
      <ForWhoSection />
      <HowItWorksSection />
      <TravelOptimizedSection />
      <CampaignsSection campaigns={campaigns} loading={loading} />
      <CTASection />
      <FooterSection />
    </div>
  );
};

export default MainPage;
